import type { OrgDisclaimerPayload } from "@/features/auth/types";
import { looksLikeOrgDisclaimerHtml } from "./orgDisclaimerHtml";

export function isOrgDisclaimerRequired(
  orgDisclaimer: OrgDisclaimerPayload | null | undefined
): boolean {
  return orgDisclaimer?.required === true;
}

/** True when an ApiError (or similar) is the org-disclaimer gate 403. */
export function isOrgDisclaimerRequiredError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const backendData = (error as { backendData?: { error_key?: string } })
    .backendData;
  return backendData?.error_key === "ORG_DISCLAIMER_REQUIRED";
}

export type OrgDisclaimerContentLocale = "english" | "french";

export interface OrgDisclaimerDisplayResolution {
  showLocaleSwitch: boolean;
  selectedKey: OrgDisclaimerContentLocale;
  displayBody: string;
  isHtml: boolean;
}

const presentBody = (value: string | null | undefined): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  return value.trim() === "" ? null : value;
};

const defaultKeyForUiLanguage = (
  uiLanguage: string
): OrgDisclaimerContentLocale => {
  const normalized = uiLanguage.toLowerCase();
  if (normalized === "fr" || normalized.startsWith("fr-")) {
    return "french";
  }
  return "english";
};

/**
 * Resolve FR/EN toggle visibility and which body string to show.
 */
export function resolveOrgDisclaimerDisplay(
  orgDisclaimer: OrgDisclaimerPayload | null | undefined,
  uiLanguage: string,
  selectedKey?: OrgDisclaimerContentLocale
): OrgDisclaimerDisplayResolution {
  const english = presentBody(orgDisclaimer?.bodies?.english);
  const french = presentBody(orgDisclaimer?.bodies?.french);
  const fallback = presentBody(orgDisclaimer?.body) ?? "";

  if (english !== null && french !== null) {
    const preferred = defaultKeyForUiLanguage(uiLanguage);
    const key =
      selectedKey === "english" || selectedKey === "french"
        ? selectedKey
        : preferred;
    const displayBody = key === "french" ? french : english;
    return {
      showLocaleSwitch: true,
      selectedKey: key,
      displayBody,
      isHtml: looksLikeOrgDisclaimerHtml(displayBody),
    };
  }

  if (english !== null) {
    return {
      showLocaleSwitch: false,
      selectedKey: "english",
      displayBody: english,
      isHtml: looksLikeOrgDisclaimerHtml(english),
    };
  }

  if (french !== null) {
    return {
      showLocaleSwitch: false,
      selectedKey: "french",
      displayBody: french,
      isHtml: looksLikeOrgDisclaimerHtml(french),
    };
  }

  return {
    showLocaleSwitch: false,
    selectedKey: defaultKeyForUiLanguage(uiLanguage),
    displayBody: fallback,
    isHtml: looksLikeOrgDisclaimerHtml(fallback),
  };
}
